/* eslint-disable @typescript-eslint/no-explicit-any */
import Emittery from 'emittery';
import { useEffect, useState } from 'react';

/**
 * Imperative API to make managing modals easily.
 *
 * assume you have defined a modal `MyModal` which adheres to the `ModalFC` interface.
 *
 * APIs available -
 * ModalsManager.show(MyModal);
 * ModalsManager.show(MyModal, {color: "red", onSubmit}); // with props
 *
 * The modal can also return a value back by calling resolve prop passed to it, usage on
 * the caller side in that case -
 * const result = await ModalsManager.showResolvable(MyModal); // with promise
 *
 */

export interface BaseModalFCProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ResolveFn<R> = (value: R) => void;
export interface ModalFC<Props extends Record<string, any>> extends React.FC<
  BaseModalFCProps & Props
> {}
export interface ResolvableModalFC<Props, ResolvedValueType> extends ModalFC<
  Props & {
    resolve: ResolveFn<ResolvedValueType>;
  }
> {}

let count = 1;
function genIncrementalId() {
  count = (count + 1) % Number.MAX_VALUE;
  return count.toString();
}

interface ModalState {
  id: string;
  modal: ModalFC<Record<string, any>>;
  props?: Record<string, unknown>;
  open: boolean;
  /** What had focus when the modal opened */
  opener: Element | null;
  onClosed?: () => void;
}

interface GlobalModalsState {
  modalsList: ModalState[];
}

export type CloseGuardResult =
  | { allowed: true }
  | { allowed: false; reason: string };

type CloseGuard = () => CloseGuardResult;

let closeGuards: CloseGuard[] = [];

// Keep a closing modal mounted through Radix's exit transition (dialog uses
// `duration-200`) before dropping it.
const CLOSE_TRANSITION_MS = 300;

const modalChangeEvent = 'change';
interface ModalsStateEventMap {
  [modalChangeEvent]: GlobalModalsState;
}
const modalsStateEmitter = new Emittery<ModalsStateEventMap>();
let globalModalsState: GlobalModalsState = { modalsList: [] };

/** The body, a disabled control, or anything detached or inert cannot take focus back */
const canTakeFocus = (element: Element | null): element is HTMLElement =>
  element instanceof HTMLElement &&
  element !== document.body &&
  element.isConnected &&
  !element.matches(':disabled') &&
  !element.closest('[inert]');

const tryFocus = (element: HTMLElement) => {
  element.focus();
  return document.activeElement === element;
};

/**
 * A modal shown here has no Radix trigger, so Radix leaves focus on the page when it closes. When focus is
 * lost, it goes back to what opened the modal if that can take it, or else to the top dialog still open
 */
function restoreFocus(opener: Element | null) {
  const active = document.activeElement;
  const isFocusLost =
    !active || active === document.body || !active.isConnected;
  if (!isFocusLost) return;
  if (canTakeFocus(opener) && tryFocus(opener)) return;
  const openDialogs = [
    ...document.querySelectorAll<HTMLElement>('[role="dialog"]'),
  ].filter((dialog) => dialog.dataset.state !== 'closed');
  openDialogs.at(-1)?.focus();
}

// Openers of closed modals, given focus back once the modal has unmounted
const focusToRestore: (Element | null)[] = [];
// A modal removed before its exit transition ends has no entry left to drop
const closeTimers = new Map<string, ReturnType<typeof setTimeout>>();
const cancelCloseTimer = (id: string) => {
  clearTimeout(closeTimers.get(id));
  closeTimers.delete(id);
};

/**
 * Two-phase close: flip `open` to false so Radix runs its exit transition and
 * restores `document.body` pointer-events, then drop the entry. Deleting outright
 * unmounts abruptly and can leave the body stuck at `pointer-events: none`,
 * freezing the whole window. A modal already closing, or gone, is left alone.
 */
function closeModal(id: string) {
  const closing = globalModalsState.modalsList.find((modal) => modal.id === id);
  if (!closing?.open) return;
  const opener = closing.opener;
  globalModalsState = {
    ...globalModalsState,
    modalsList: globalModalsState.modalsList.map((modal) =>
      modal.id === id ? { ...modal, open: false } : modal
    ),
  };
  modalsStateEmitter.emit(modalChangeEvent, globalModalsState);

  cancelCloseTimer(id);
  const timer = setTimeout(() => {
    closeTimers.delete(id);
    globalModalsState = {
      ...globalModalsState,
      modalsList: globalModalsState.modalsList.filter(
        (modal) => modal.id !== id
      ),
    };
    focusToRestore.push(opener);
    modalsStateEmitter.emit(modalChangeEvent, globalModalsState);
  }, CLOSE_TRANSITION_MS);
  closeTimers.set(id, timer);
  closing.onClosed?.();
}

export const DisplayModals = () => {
  const [modalsState, setModalsState] =
    useState<GlobalModalsState>(globalModalsState);
  useEffect(() => {
    // force rerender when modals state changes
    const unsub = modalsStateEmitter.on(modalChangeEvent, (state) => {
      setModalsState(state);
    });
    return () => {
      unsub();
    };
  }, []);
  useEffect(() => {
    for (const opener of focusToRestore.splice(0)) restoreFocus(opener);
  }, [modalsState]);
  return modalsState.modalsList.map((modalState) => {
    const Modal = modalState.modal;
    return (
      <Modal
        key={modalState.id}
        {...modalState.props}
        open={modalState.open}
        // A caller's own callback, such as a hash-opened modal clearing its hash, runs before the shared close
        onOpenChange={(open) => {
          const ownOnOpenChange = modalState.props?.onOpenChange;
          if (typeof ownOnOpenChange === 'function') ownOnOpenChange(open);
          if (!open) closeModal(modalState.id);
        }}
      />
    );
  });
};

export const isModalOpen = <Props extends Record<string, any>>(
  Modal: ModalFC<Props>
) => {
  // Ignore a modal mid-close so it can be reopened during its exit transition.
  return globalModalsState.modalsList.some(
    (modal) => modal.modal === Modal && modal.open
  );
};

/** Reactively track whether any modal is open — used by click-through windows
 *  (e.g. the Hummingbird overlay) to hold mouse capture while a dialog is up. */
export const useHasOpenModals = (): boolean => {
  const [hasOpen, setHasOpen] = useState(
    () => globalModalsState.modalsList.length > 0
  );
  useEffect(() => {
    const unsub = modalsStateEmitter.on(modalChangeEvent, (state) =>
      setHasOpen(state.modalsList.length > 0)
    );
    return () => unsub();
  }, []);
  return hasOpen;
};

interface ModalShowOptions {
  unique?: boolean;
  /** What gets focus back when the modal closes, when that is not what has focus now */
  opener?: Element | null;
  /** Called once the modal starts closing, whatever closed it: the person, `hide`, or `closeAll` */
  onClosed?: () => void;
}

interface ModalReturnControls {
  hide: () => void;
}

/**
 * only for modals post login, to guranatee availability of workspace related hooks
 */
export const ModalsManager = {
  show<Props extends Record<string, any>>(
    Modal: ModalFC<Props>,
    props?: Omit<Props, 'open' | 'onOpenChange'>,
    options?: ModalShowOptions
  ): ModalReturnControls | undefined {
    if (options?.unique && isModalOpen(Modal as any)) {
      return;
    }
    const id = genIncrementalId();
    const existingModalsList: ModalState[] = globalModalsState.modalsList;
    globalModalsState = {
      ...globalModalsState,
      modalsList: [
        ...existingModalsList,
        {
          id,
          modal: Modal as any,
          props,
          open: true,
          opener: options?.opener ?? document.activeElement,
          onClosed: options?.onClosed,
        },
      ],
    };
    modalsStateEmitter.emit(modalChangeEvent, globalModalsState);
    return {
      hide: () => {
        closeModal(id);
      },
    };
  },
  update<Props extends Record<string, any>>(
    Modal: ModalFC<Props>,
    props: Omit<Props, 'open' | 'onOpenChange'>
  ) {
    const modal = globalModalsState.modalsList.find(
      (modal) => modal.modal === Modal
    );
    if (!modal) return;

    globalModalsState = {
      ...globalModalsState,
      modalsList: globalModalsState.modalsList.map((m) =>
        m.modal === Modal ? { ...m, props } : m
      ),
    };
    modalsStateEmitter.emit(modalChangeEvent, globalModalsState);
  },
  showResolvable<Props, R>(
    Modal: ResolvableModalFC<Props, R>,
    props?: Omit<Props, 'open' | 'onOpenChange' | 'resolve'>,
    options?: ModalShowOptions
  ): Promise<R> {
    return new Promise((resolve) => {
      ModalsManager.show<Props & { resolve: ResolveFn<R> }>(
        Modal,
        { ...props, resolve } as any,
        options
      );
    });
  },
  hideAll() {
    const removed = globalModalsState.modalsList;
    for (const modal of removed) cancelCloseTimer(modal.id);
    globalModalsState = {
      ...globalModalsState,
      modalsList: [],
    };
    modalsStateEmitter.emit(modalChangeEvent, globalModalsState);
    for (const modal of removed) if (modal.open) modal.onClosed?.();
  },
  closeAll() {
    for (const modal of globalModalsState.modalsList) closeModal(modal.id);
  },
  onCloseGuard(guard: CloseGuard): () => void {
    closeGuards.push(guard);
    return () => {
      closeGuards = closeGuards.filter((g) => g !== guard);
    };
  },
  canCloseAll(): CloseGuardResult {
    for (const guard of closeGuards) {
      const result = guard();
      if (!result.allowed) return result;
    }
    return { allowed: true };
  },
  hide(Modal: ModalFC<Record<string, any>>) {
    const removed = globalModalsState.modalsList.filter(
      (modal) => modal.modal === Modal
    );
    for (const modal of removed) {
      cancelCloseTimer(modal.id);
      focusToRestore.push(modal.opener);
    }
    globalModalsState = {
      ...globalModalsState,
      modalsList: globalModalsState.modalsList.filter(
        (modal) => modal.modal !== Modal
      ),
    };
    modalsStateEmitter.emit(modalChangeEvent, globalModalsState);
    for (const modal of removed) if (modal.open) modal.onClosed?.();
  },
  isOpen: isModalOpen,
  onOpenChange(listener: (hasOpenModal: boolean) => void) {
    let lastValue = globalModalsState.modalsList.length > 0;
    return modalsStateEmitter.on(modalChangeEvent, (state) => {
      const hasOpenModal = state.modalsList.length > 0;
      if (hasOpenModal === lastValue) return;
      lastValue = hasOpenModal;
      listener(hasOpenModal);
    });
  },
};
